const CURRENCY_TYPE_TRADING = 3;

function asAccountId(value) {
  if (value == null || value === "") return null;
  const text = String(value).trim();
  if (!text) return null;
  // Names like "Ali ibrahim" must never be treated as an MT5 account id.
  if (/[A-Za-z]/.test(text) && /\s/.test(text)) return null;
  return text;
}

function findTradingId(node, depth = 0, seen = new Set()) {
  if (node == null || depth > 8 || typeof node !== "object") return null;
  if (seen.has(node)) return null;
  seen.add(node);

  const direct = asAccountId(
    node.TRADING_ID ??
      node.trading_id ??
      node.tradingId ??
      node.MT5_LOGIN ??
      node.mt5_login ??
      node.LOGIN ??
      node.login
  );
  if (direct) return direct;

  const nested = [
    node.account_information,
    node.accountInformation,
    node.ACCOUNT_INFORMATION,
    node.account,
    node.ACCOUNT,
  ];
  for (const child of nested) {
    const found = findTradingId(child, depth + 1, seen);
    if (found) return found;
  }
  return null;
}

function walletCurrency(wallet) {
  if (!wallet || typeof wallet !== "object") return null;
  return wallet.currencies || wallet.CURRENCY || wallet.currency || null;
}

export function isTradingWallet(wallet) {
  const currency = walletCurrency(wallet);
  const type = Number(currency?.TYPE ?? currency?.type);
  if (type === CURRENCY_TYPE_TRADING) return true;
  const code = String(currency?.CURRENCY_CODE || currency?.currencyCode || "").toUpperCase();
  const symbol = String(currency?.CURRENCY_SYMBOL || currency?.currencySymbol || "").toUpperCase();
  return code === "MT5" || symbol === "MT5";
}

export function extractWalletTradingId(wallet) {
  return findTradingId(wallet);
}

function partyUserName(detailsData) {
  return (
    detailsData?.receiverName ||
    detailsData?.senderName ||
    detailsData?.receiverWalletLabel ||
    detailsData?.senderWalletLabel ||
    null
  );
}

/**
 * Resolve sender/receiver for Internal transfer MT5:
 * - MT5 -> user: sender is account id, receiver is the user
 * - user -> MT5: sender is the user, receiver is account id
 * - MT5 -> MT5: both sides are account ids
 */
export function resolveInternalTransferParties(raw = {}, detailsData = {}) {
  const receiverWallet = raw.receiver_wallet || raw.RECEIVER_WALLET || null;
  const senderWallet = raw.sender_wallet || raw.SENDER_WALLET || null;

  const receiverId =
    asAccountId(detailsData?.receiverTradingId) || extractWalletTradingId(receiverWallet);
  const senderId =
    asAccountId(detailsData?.senderTradingId) || extractWalletTradingId(senderWallet);
  const userName = partyUserName(detailsData);

  const senderIsMt5 = isTradingWallet(senderWallet) || (Boolean(senderId) && !isTradingWallet(receiverWallet));
  const receiverIsMt5 = isTradingWallet(receiverWallet) || (Boolean(receiverId) && !isTradingWallet(senderWallet));

  if (senderIsMt5 && receiverIsMt5) {
    return {
      senderValue: senderId || userName || "-",
      receiverValue: receiverId || "-",
      senderLabelKey: "sender",
      senderLabelDefault: "Sender",
      receiverLabelKey: "account_id",
      receiverLabelDefault: "Account number",
      senderIcon: "fa-hashtag",
      receiverIcon: "fa-hashtag",
    };
  }

  if (senderIsMt5 && !receiverIsMt5) {
    return {
      senderValue: senderId || "-",
      receiverValue: userName || "-",
      senderLabelKey: "account_id",
      senderLabelDefault: "Account ID",
      receiverLabelKey: "receiver",
      receiverLabelDefault: "Receiver",
      senderIcon: "fa-hashtag",
      receiverIcon: "fa-user",
    };
  }

  return {
    senderValue: userName || "-",
    receiverValue: receiverId || senderId || "-",
    senderLabelKey: "sender",
    senderLabelDefault: "Sender",
    receiverLabelKey: "account_id",
    receiverLabelDefault: "Account number",
    senderIcon: "fa-building-columns",
    receiverIcon: "fa-hashtag",
  };
}
